import { ThreatLinkType } from '@/generated/enums'
import {
  useCreateEntityLinkMutation,
  useValidateEntityLinkLazyQuery,
  ValidateEntityLinkQueryVariables,
} from '@/generated/graphql'
import { formatSnakeStringWithCapitalization } from '@/utils/string_utils'
import { DOPPEL_TEXT_GREY, FONT_SIZE_SMALL } from '@/utils/style'
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Switch,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import DoppelModal from '../doppel_design/doppel_modal'
import DoppelPrimaryButton from '../doppel_design/doppel_primary_button'
import { Dropdown } from '../doppel_design/dropdown'
import ThreatGraph from './threat_graph'
import {
  processResponseToThreatGraph,
  ThreatGraphLink,
  ThreatGraphNode,
} from './threat_graph_utils'

type ThreatGraphAddLinkModalProps = {
  sourceNode?: ThreatGraphNode // if provided, will be populated as the root entity for the link
  sourceEntityLocked?: boolean // if true, the root entity cannot be changed
  isOpen: boolean
  onClose: () => void
  onSubmit?: () => void
  zIndex?: number
}

type NodeInput = {
  input_value: string
  // some nodes have non-unique values (e.g. social media users, advertisers)
  // this is more reliable if we have the data
  entity_data_id?: string
}

const hashLink = (parent: string, child: string, type: ThreatLinkType): string => {
  return `${parent}__${child}__${type}`
}

/**
 * Helper to put (source, target) in (parent, child) order
 */
const orderLinkInputs = (
  source: string,
  target: string,
  sourceRoleInLink: 'parent' | 'child',
): [string, string] => {
  if (sourceRoleInLink === 'parent') {
    return [source, target]
  } else {
    return [target, source]
  }
}

const ThreatGraphAddLinkModal = ({
  sourceNode,
  isOpen,
  onClose,
  onSubmit,
  zIndex,
}: ThreatGraphAddLinkModalProps) => {
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const sourceValueDefault = sourceNode?.entity?.value // if set, will lock the source entity field
  const [sourceEntityValue, setSourceEntityValue] = useState<string>(sourceValueDefault)
  const [sourceEntityFormValue, setSourceEntityFormValue] =
    useState<string>(sourceValueDefault)
  const [selectedType, setSelectedType] = useState<ThreatLinkType>()
  const [sourceRoleInLink, setSourceRoleInLink] = useState<'child' | 'parent'>('parent')
  const [targetEntityValues, setTargetEntityValues] = useState<Set<string>>(new Set())
  const [targetEntityFormValue, setTargetEntityFormValue] = useState<string>()

  // these come from backend responses, and are used to construct the preview graph
  const valueToNodeMap = useRef<Map<string, ThreatGraphNode>>(new Map()) // key is just entity value
  const valueToLinkMap = useRef<Map<string, ThreatGraphLink>>(new Map()) // key is hashNodePair

  useEffect(() => {
    // resets all state variables when closing/opening
    setSourceEntityValue(sourceValueDefault)
    setSourceEntityFormValue(sourceValueDefault || '')
    setSelectedType(undefined)
    setSourceRoleInLink('parent')
    setTargetEntityValues(new Set())
    setTargetEntityFormValue(undefined)
    valueToNodeMap.current.clear()
    valueToLinkMap.current.clear()
    if (sourceValueDefault) {
      valueToNodeMap.current.set(sourceValueDefault, sourceNode)
    }
  }, [isOpen, sourceNode])

  const [validateEntityLink] = useValidateEntityLinkLazyQuery()
  const [createEntityLink] = useCreateEntityLinkMutation()

  const callValidation = async (
    parentNodeInput: NodeInput,
    childNodeInput: NodeInput | undefined,
    linkType: ThreatLinkType | undefined,
  ): Promise<[ThreatGraphNode[], ThreatGraphLink[]] | undefined> => {
    const variables: ValidateEntityLinkQueryVariables = {
      parent_node: parentNodeInput,
    }
    if (childNodeInput) {
      variables.child_node = childNodeInput
      variables.link_type = linkType
    }
    const result = await validateEntityLink({ variables })
    if (!result.data?.validate_entity_link.is_valid) {
      const error =
        result.error?.graphQLErrors?.[0].message ||
        result.data?.validate_entity_link.error
      throw new Error(`Invalid entity/link: ${error || 'Unknown error'}`)
    }

    const graph = result.data.validate_entity_link.graph
    const { nodeList, linkList } = processResponseToThreatGraph(graph)

    return [nodeList, linkList]
  }

  /**
   * Helper for determining information about a node to pass to GCF
   * so we can uniquely identify the entity
   */
  const constructNodeInput = (value: string): NodeInput => {
    const node = valueToNodeMap.current.get(value)
    if (!node) {
      return { input_value: value }
    }
    return {
      input_value: value,
      entity_data_id: node.entity.entity_data_ids[0],
    }
  }

  /**
   * @throws Error if the entity/link is invalid
   */
  const validateOrCreateLink = async (
    operation: 'validate' | 'create',
    sourceValue: string,
    targetValue: string,
    linkType: ThreatLinkType,
    sourceRoleInLink: 'parent' | 'child',
  ): Promise<void> => {
    const [parentValue, childValue] = orderLinkInputs(
      sourceValue,
      targetValue,
      sourceRoleInLink,
    )
    const allValuesInCache =
      valueToNodeMap.current.has(parentValue) &&
      valueToNodeMap.current.has(childValue) &&
      valueToLinkMap.current.has(hashLink(parentValue, childValue, linkType))

    const parentNodeInput = constructNodeInput(parentValue)
    const childNodeInput = constructNodeInput(childValue)

    switch (operation) {
      case 'validate': {
        if (allValuesInCache) return

        const [nodes, links] = await callValidation(
          parentNodeInput,
          childNodeInput,
          linkType,
        )
        if (nodes.length !== 2 || links.length !== 1) {
          throw new Error(
            'Invalid entity/link: expected exactly two nodes and one link',
          )
        }
        const [link] = links
        const parentEntity = nodes.find((n) => n.id === link.parent)
        const childEntity = nodes.find((n) => n.id === link.child)
        if (!parentEntity || !childEntity) {
          throw new Error('Invalid entity/link: target entity not found')
        }
        valueToNodeMap.current.set(parentValue, parentEntity)
        valueToNodeMap.current.set(childValue, childEntity)
        valueToLinkMap.current.set(hashLink(parentValue, childValue, linkType), link)
        break
      }
      case 'create': {
        if (!allValuesInCache) {
          throw new Error('Entity/link not validated')
        }

        const result = await createEntityLink({
          variables: {
            // may need to pass in alert_id when available for speed
            parent_node: parentNodeInput,
            child_node: childNodeInput,
            link_type: linkType,
          },
        })
        if (!result.data?.create_entity_link.is_valid) {
          const error =
            result.errors?.[0].message || result.data?.create_entity_link.error
          throw new Error(`Invalid entity/link: ${error || 'Unknown error'}`)
        }
        break
      }
    }
  }

  const handleAddSource = async (sourceValue: string) => {
    if (!sourceValue) return
    setLoading(true)
    sourceValue = sourceValue.trim()
    try {
      if (valueToNodeMap.current.has(sourceValue)) {
        return
      }
      if (targetEntityValues.size > 0 && selectedType) {
        // switching source node; redo all links
        await Promise.all(
          [...targetEntityValues].map((targetValue) =>
            validateOrCreateLink(
              'validate',
              sourceValue,
              targetValue,
              selectedType,
              sourceRoleInLink,
            ),
          ),
        )
      } else {
        // just validate source node
        const [nodes, _] = await callValidation(
          constructNodeInput(sourceValue),
          undefined,
          undefined,
        )
        if (nodes.length !== 1) {
          throw new Error('Invalid entity/link: expected exactly one node')
        }
        const [node] = nodes
        valueToNodeMap.current.set(sourceValue, node)
      }

      if (valueToNodeMap.current.has(sourceValue)) {
        setSourceEntityValue(sourceValue)
      } else {
        throw new Error('Node not successfully fetched')
      }
    } catch (error) {
      toast({
        title: 'Error adding source node to preview',
        description: error.message,
        status: 'error',
        isClosable: true,
      })
      return
    } finally {
      setLoading(false)
    }
  }

  const handleAddTarget = async (entityValueInput: string) => {
    if (!sourceEntityValue || !selectedType) return
    setLoading(true)
    try {
      const entityValues = entityValueInput.trim().split('\n')
      await Promise.all(
        entityValues.map((targetValue) =>
          validateOrCreateLink(
            'validate',
            sourceEntityValue,
            targetValue,
            selectedType,
            sourceRoleInLink,
          ),
        ),
      )

      if (
        valueToNodeMap.current.has(sourceEntityValue) &&
        entityValues.every((value) => valueToNodeMap.current.has(value)) &&
        entityValues.every((value) =>
          valueToLinkMap.current.has(
            hashLink(
              ...orderLinkInputs(sourceEntityValue, value, sourceRoleInLink),
              selectedType,
            ),
          ),
        )
      ) {
        setTargetEntityValues((prev) => new Set([...prev, ...entityValues]))
        setTargetEntityFormValue('')
      } else {
        throw new Error('Link not successfully fetched')
      }
    } catch (error) {
      toast({
        title: 'Error adding target node to preview',
        description: error.message,
        status: 'error',
        isClosable: true,
      })
      return
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    await Promise.all(
      [...targetEntityValues].map((targetValue) =>
        validateOrCreateLink(
          'create',
          sourceEntityValue,
          targetValue,
          selectedType,
          sourceRoleInLink,
        ),
      ),
    )
    toast({
      title: 'Links created successfully',
      description: 'Changes should be reflected shortly.',
      status: 'success',
      isClosable: true,
    })
    onSubmit?.()
    onClose()
  }

  // slightly jank, but re-add all target entities when link changes
  useEffect(() => {
    if (selectedType && sourceEntityValue && targetEntityValues.size > 0) {
      handleAddTarget([...targetEntityValues].join('\n'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType, sourceRoleInLink])

  const previewGraph = useMemo(() => {
    const nodes: ThreatGraphNode[] = []
    const links: ThreatGraphLink[] = []

    // Check that all items are in map, else return
    if (
      ![sourceEntityValue, ...targetEntityValues].every((value) =>
        valueToNodeMap.current.has(value),
      ) ||
      ![...targetEntityValues].every((value) =>
        valueToLinkMap.current.has(
          hashLink(
            ...orderLinkInputs(sourceEntityValue, value, sourceRoleInLink),
            selectedType,
          ),
        ),
      )
    ) {
      return { nodes, links }
    }

    // Add source node if we have it in our map
    const sourceNode = valueToNodeMap.current.get(sourceEntityValue)
    if (sourceNode) {
      nodes.push({ ...sourceNode, currentNode: true })
    }

    if (!sourceNode || !selectedType || targetEntityValues.size === 0) {
      return { nodes, links }
    }

    // Add target nodes and create links
    targetEntityValues.forEach((targetValue) => {
      const targetNode = valueToNodeMap.current.get(targetValue)
      const link = valueToLinkMap.current.get(
        hashLink(
          ...orderLinkInputs(sourceEntityValue, targetValue, sourceRoleInLink),
          selectedType,
        ),
      )
      if (targetNode && link) {
        nodes.push(targetNode)
        links.push(link)
      }
    })

    return { nodes, links }
  }, [sourceEntityValue, selectedType, sourceRoleInLink, targetEntityValues])

  const body = (
    <VStack align="stretch" spacing={4}>
      <Text color={DOPPEL_TEXT_GREY} fontSize={FONT_SIZE_SMALL} mb={1}>
        Link a source entity to one or more target entities. Entities may be input as an
        alert ID, or an entity value (e.g. URL, email address, phone #). This does{' '}
        <b>not</b> create a link in the Alert Links tab.
      </Text>

      <FormControl isRequired>
        <FormLabel>Source Entity</FormLabel>

        <Text color={DOPPEL_TEXT_GREY} fontSize={FONT_SIZE_SMALL} mb={1}>
          Click &lsquo;{sourceEntityValue ? 'Update' : 'Add'}&rsquo; for changes to be
          reflected.
        </Text>

        <HStack width="100%">
          <Input
            isDisabled={!!sourceValueDefault}
            onChange={(e) => setSourceEntityFormValue(e.target.value)}
            placeholder="Enter source entity"
            value={sourceEntityFormValue}
          />

          <DoppelPrimaryButton
            isDisabled={!!sourceValueDefault}
            onClick={() => handleAddSource(sourceEntityFormValue)}
          >
            {sourceEntityValue ? 'Update' : 'Add'}
          </DoppelPrimaryButton>
        </HStack>
      </FormControl>

      <FormControl isRequired>
        <FormLabel>Link Type</FormLabel>

        <Text color={DOPPEL_TEXT_GREY} fontSize={FONT_SIZE_SMALL} mb={1}>
          Links point from Source to Target by default. Type and direction will be the
          same for all added links.
        </Text>

        <HStack spacing={4}>
          <Dropdown
            items={Object.values(ThreatLinkType)
              .map((type) => ({
                label: formatSnakeStringWithCapitalization(type),
                value: type,
              }))
              .sort((a, b) => a.label.localeCompare(b.label))}
            menuLabel={
              selectedType
                ? formatSnakeStringWithCapitalization(selectedType)
                : 'Select Type'
            }
            onSelect={(val) => setSelectedType(val as ThreatLinkType)}
            selectedValue={selectedType}
          />

          <Flex alignItems="center">
            <Text mr={2}>Flip Direction:</Text>

            <Switch
              isChecked={sourceRoleInLink === 'child'}
              onChange={() =>
                setSourceRoleInLink(sourceRoleInLink === 'child' ? 'parent' : 'child')
              }
            />
          </Flex>
        </HStack>
      </FormControl>

      <FormControl>
        <FormLabel>Add Target Entity</FormLabel>

        <Text color={DOPPEL_TEXT_GREY} fontSize={FONT_SIZE_SMALL} mb={1}>
          Click &lsquo;Add&rsquo; to add link(s) to the preview. Must select a link type
          first.
        </Text>

        <HStack>
          <Textarea
            minHeight="60px"
            onChange={(e) => setTargetEntityFormValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.ctrlKey && e.key === 'Enter') {
                handleAddTarget(targetEntityFormValue)
              }
            }}
            placeholder="Enter newline-separated Alert IDs or entity values (e.g. URL, email address, phone #)"
            value={targetEntityFormValue}
          />

          <DoppelPrimaryButton onClick={() => handleAddTarget(targetEntityFormValue)}>
            Add
          </DoppelPrimaryButton>
        </HStack>
      </FormControl>

      <Box>
        <FormLabel>Preview New Link(s)</FormLabel>

        <Text color={DOPPEL_TEXT_GREY} fontSize={FONT_SIZE_SMALL} mb={1}>
          This reflects the links that will be added on submission.
        </Text>

        <Box borderRadius="lg" borderWidth="1px" height={260} p={4}>
          <ThreatGraph
            links={previewGraph.links}
            loading={loading}
            nodes={previewGraph.nodes}
          />
        </Box>
      </Box>
    </VStack>
  )
  return (
    <DoppelModal
      body={body}
      captureKeyDown
      closeOnOverlayClick={false}
      isAsync
      isOpen={isOpen}
      onClose={onClose}
      primaryAction={handleSubmit}
      primaryLabel="Submit"
      size="lg"
      title="Add Threat Links"
      zIndex={zIndex}
    />
  )
}

export default ThreatGraphAddLinkModal
